/**
 * Right to left on native without flipping twice (WP12 finding, fixed with
 * the user test fixes 2026-09-25).
 *
 * With `I18nManager.isRTL` true, React Native already lays a `row` out from
 * the right, swaps `left` and `right` in styles, and puts a stack header's
 * back button on the right. A component that also reverses its row by hand
 * (`isRTL ? 'row-reverse' : 'row'`) turns Arabic back into the English
 * order, and an absolutely placed `left: 0` lands on the right edge. The
 * manual reversal is only needed while the locale and native layout disagree
 * in either direction (a language switch waits for a restart; see
 * apps/mobile/AGENTS.md, Arabic / RTL Design Rules).
 */
import { I18nManager, Platform } from 'react-native';

/** Whether native layout already runs right to left. */
export function isNativeLayoutRTL(): boolean {
  return Boolean(I18nManager?.isRTL);
}

/**
 * Rows inherit the native root direction. Reverse only while it disagrees
 * with the selected locale, in either direction during a language switch.
 * Web inherits document.dir, which useLocale updates without a native reload.
 * Do not use this inside a subtree with its own explicit `direction`.
 */
export function localeRowDirection(localeRTL: boolean): 'row' | 'row-reverse' {
  if (Platform.OS === 'web') return 'row';
  return localeRTL !== isNativeLayoutRTL() ? 'row-reverse' : 'row';
}

/** Whether a layout that follows the locale reads right to left on screen. */
export function readsRightToLeft(localeRTL: boolean): boolean {
  return localeRTL;
}

/**
 * Pins an absolutely placed box to the physical left edge in either
 * direction (a measured `onLayout.x` plus `translateX` is physical).
 */
export function physicalLeftEdge(): { left: number } | { right: number } {
  if (!isNativeLayoutRTL()) return { left: 0 };
  const constants = (I18nManager as unknown as { getConstants?: () => { doLeftAndRightSwapInRTL?: boolean } })
    .getConstants?.();
  return constants?.doLeftAndRightSwapInRTL === false ? { left: 0 } : { right: 0 };
}
